import { alpha, createTheme, type PaletteMode } from '@mui/material/styles';
import type { ColorTheme } from '../../api/src/auth/user-preferences';

declare module '@mui/material/styles' {
  interface Theme {
    colorTheme: ColorTheme;
  }
  interface ThemeOptions {
    colorTheme?: ColorTheme;
  }
}

const common = {
  mint: '#9cf5c5',
  ink: '#0b0c10',
  surface: '#15161c',
  text: '#f4f2ed',
};

export const COLOR_THEME_LABELS: Record<ColorTheme, string> = {
  light: 'Светлая',
  dark: 'Тёмная',
  graphite: 'Графитовая',
};

export function createAppTheme(colorTheme: ColorTheme) {
  const graphite = colorTheme === 'graphite';
  const mode: PaletteMode = colorTheme === 'light' ? 'light' : 'dark';
  const dark = mode === 'dark';
  const background = graphite
    ? { default: '#232323', paper: '#343434' }
    : dark
      ? { default: common.ink, paper: common.surface }
      : { default: '#f4f2ee', paper: '#fffdf9' };
  const divider = graphite ? '#505050' : dark ? 'rgba(255,255,255,.12)' : 'rgba(11,12,16,.13)';
  const primary = graphite ? '#E2E2E4' : dark ? '#8ab4f8' : '#2563eb';
  const elevated = graphite ? '#3A3A3A' : background.paper;
  const focus = graphite ? { outline: `2px solid ${primary}`, outlineOffset: 2 } : {};

  return createTheme({
    colorTheme,
    spacing: 7,
    palette: {
      mode,
      primary: {
        main: primary,
        contrastText: graphite ? background.default : dark ? common.ink : '#ffffff',
      },
      secondary: {
        main: graphite ? '#D7D7D9' : dark ? common.mint : '#16845c',
        contrastText: dark ? common.ink : '#ffffff',
      },
      background,
      text: graphite
        ? { primary: '#F2F2F2', secondary: '#B9B9BD' }
        : dark
          ? { primary: common.text, secondary: '#a2a1aa' }
          : { primary: '#17171c', secondary: '#66656d' },
      divider,
      success: { main: graphite ? '#A9DFC3' : dark ? '#72d6a2' : '#27865a' },
      warning: { main: graphite ? '#F0CC91' : dark ? '#f1b663' : '#b66a16' },
      error: { main: graphite ? '#EF9B9F' : dark ? '#ff7b72' : '#bd3d45' },
      info: { main: graphite ? '#C2ACE3' : dark ? '#79c7ff' : '#267eac' },
      action: {
        hover: dark ? 'rgba(255,255,255,.055)' : 'rgba(11,12,16,.045)',
        selected: graphite
          ? 'rgba(255,255,255,.12)'
          : dark
            ? 'rgba(138,180,248,.14)'
            : 'rgba(37,99,235,.09)',
      },
    },
    shape: { borderRadius: 4 },
    typography: {
      fontWeightMedium: 500,
      fontWeightBold: graphite ? 600 : 700,
      fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: {
        fontSize: 'clamp(2rem, 4vw, 3.75rem)',
        fontWeight: graphite ? 500 : 720,
        letterSpacing: '-0.055em',
        lineHeight: 1.02,
      },
      h2: {
        fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)',
        fontWeight: graphite ? 500 : 710,
        letterSpacing: '-0.04em',
        lineHeight: 1.08,
      },
      h3: { fontSize: '1.08rem', fontWeight: graphite ? 500 : 700, letterSpacing: '-0.02em' },
      button: { textTransform: 'none', fontWeight: graphite ? 500 : 690, letterSpacing: '-0.01em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--app-background': background.default,
            '--app-background-image': graphite
              ? 'none'
              : `radial-gradient(circle at 88% -12%, ${dark ? 'rgba(255,51,102,.14)' : 'rgba(230,45,93,.1)'}, transparent 30rem)`,
            '--app-link': primary,
            '--app-selection': graphite ? '#656565' : '#ff3366',
            '--app-selection-text': '#ffffff',
          },
          body: {
            backgroundColor: background.default,
            transition: 'background-color 160ms ease, color 160ms ease',
          },
          ...(graphite ? { 'a:focus-visible': focus } : {}),
        },
      },
      MuiButtonBase: { styleOverrides: { root: { '&.Mui-focusVisible': focus } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 34,
            borderRadius: 7,
            padding: '6px 13px',
            '&.MuiButton-containedPrimary': {
              boxShadow: graphite
                ? 'none'
                : dark
                  ? '0 9px 24px rgba(138,180,248,.12)'
                  : '0 9px 24px rgba(37,99,235,.13)',
            },
          },
          sizeSmall: { minHeight: 29, padding: '4px 10px' },
          sizeLarge: { minHeight: 40, padding: '8px 16px' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${divider}`,
            borderRadius: 11,
            boxShadow: graphite
              ? 'none'
              : dark
                ? '0 18px 48px rgba(0,0,0,.18)'
                : '0 16px 44px rgba(28,27,31,.055)',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { padding: 18, '&:last-child': { paddingBottom: 18 } },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiPopover: {
        styleOverrides: {
          paper: graphite
            ? {
                backgroundColor: elevated,
                border: `1px solid ${divider}`,
                boxShadow: '0 8px 24px rgba(0,0,0,.24)',
              }
            : {},
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: graphite ? '#2B2B2B' : alpha(background.paper, dark ? 0.72 : 0.82),
            ...(graphite
              ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: '#858585' } }
              : {}),
            '&.MuiInputBase-sizeSmall input': { paddingTop: 8.5, paddingBottom: 8.5 },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { fontSize: 14 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 7, padding: 7 } },
      },
      MuiChip: {
        styleOverrides: {
          root: { height: 28, borderRadius: 6, fontWeight: graphite ? 500 : 650 },
          sizeSmall: { height: 24 },
          label: { paddingLeft: 9, paddingRight: 9 },
        },
      },
      MuiTabs: { styleOverrides: { root: { minHeight: 42 } } },
      MuiTab: {
        styleOverrides: {
          root: { minHeight: 42, minWidth: 72, padding: '7px 11px', textTransform: 'none' },
        },
      },
      MuiTableCell: {
        styleOverrides: { root: { borderColor: divider, padding: '9px 11px' } },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 9, padding: '6px 11px' } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${divider}`,
            borderRadius: 12,
            ...(graphite
              ? { backgroundColor: elevated, boxShadow: '0 12px 40px rgba(0,0,0,.28)' }
              : {}),
          },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { padding: '17px 18px 10px' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '12px 18px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '10px 18px 17px' } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            ...(graphite
              ? { backgroundColor: elevated, color: '#F2F2F2', border: `1px solid ${divider}` }
              : {}),
          },
        },
      },
    },
  });
}

// Kept for focused component tests and consumers that need a stable, non-reactive theme.
export const theme = createAppTheme('light');
